/**
 * Componente para visualizar reportes de tiempo de uso y auditoría
 */
import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Shield, Clock, FileText, Calendar, TrendingUp, Users, Activity, AlertCircle, Lock, Wifi, WifiOff, RefreshCw } from 'lucide-react';
import auditService from '../services/auditService';
import presenceService from '../services/presenceService';
import { format, subDays, subMonths, startOfMonth, endOfMonth, startOfWeek, endOfWeek } from 'date-fns';
import { es } from 'date-fns/locale';
import { useAuth } from '../contexts/AuthContext';

const AuditManager = () => {
  const { user } = useAuth();
  
  // Verificar si el usuario es sysadmin o tiene la categoría sysadmin
  const isSysadmin = useMemo(() => {
    if (!user) {
      return false;
    }
    
    // Si no tiene categoría, no es sysadmin
    if (!user.category) {
      return false;
    }
    
    // Verificar si la categoría se llama "sysadmin" (case insensitive)
    // Manejar diferentes estructuras: user.category.name o user.category directamente
    let categoryName = null;
    if (typeof user.category === 'string') {
      categoryName = user.category;
    } else if (user.category && typeof user.category === 'object') {
      categoryName = user.category.name || user.category.category_name;
    }
    
    return categoryName && typeof categoryName === 'string' && categoryName.toLowerCase() === 'sysadmin';
  }, [user]);

  // Verificar permisos - sysadmin tiene acceso completo, otros usuarios según permisos
  const canViewAudit = useMemo(() => {
    // Si es sysadmin, tiene acceso completo
    if (isSysadmin) {
      return true;
    }
    // Si no tiene categoría o permisos, denegar acceso
    if (!user || !user.category || !user.category.permissions) {
      return false;
    }
    // Verificar si tiene el permiso específico marcado como true
    return user.category.permissions['audit.view'] === true;
  }, [user, isSysadmin]);
  
  const canViewTimeUsage = useMemo(() => {
    // Si es sysadmin, tiene acceso completo
    if (isSysadmin) {
      return true;
    }
    if (!user || !user.category || !user.category.permissions) {
      return false;
    }
    return user.category.permissions['audit.time_usage'] === true;
  }, [user, isSysadmin]);
  
  const canViewAllUsersTimeUsage = useMemo(() => {
    // Si es sysadmin, puede ver todos los usuarios
    if (isSysadmin) {
      return true;
    }
    if (!user || !user.category || !user.category.permissions) {
      return false;
    }
    return user.category.permissions['audit.time_usage.all_users'] === true;
  }, [user, isSysadmin]);
  
  const canViewLogs = useMemo(() => {
    // Si es sysadmin, tiene acceso completo
    if (isSysadmin) {
      return true;
    }
    if (!user || !user.category || !user.category.permissions) {
      return false;
    }
    return user.category.permissions['audit.logs'] === true;
  }, [user, isSysadmin]);
  
  const canViewAllUsersLogs = useMemo(() => {
    // Si es sysadmin, puede ver todos los logs
    if (isSysadmin) {
      return true;
    }
    if (!user || !user.category || !user.category.permissions) {
      return false;
    }
    return user.category.permissions['audit.logs.all_users'] === true;
  }, [user, isSysadmin]);
  
  // Si no tiene permiso para ver auditoría, mostrar mensaje
  if (!canViewAudit) {
    return (
      <div className="space-y-6">
        <motion.div
          className="bg-white border border-gray-200 rounded-xl p-12 shadow-sm text-center"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Lock className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Acceso Restringido</h2>
          <p className="text-gray-600">
            No tienes permisos para acceder a la sección de Auditoría.
            <br />
            Contacta a tu administrador para solicitar acceso.
          </p>
        </motion.div>
      </div>
    );
  }
  const [activeTab, setActiveTab] = useState('time-usage'); // 'time-usage', 'audit-logs' o 'online-users'
  const [timeUsageData, setTimeUsageData] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [loadingOnlineUsers, setLoadingOnlineUsers] = useState(false);
  const [totalUsage, setTotalUsage] = useState(null);
  const [daysWithoutActivity, setDaysWithoutActivity] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateFilter, setDateFilter] = useState('all'); // 'last_week', 'last_month', 'current_month', 'all', 'custom'
  const [customDateFrom, setCustomDateFrom] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [customDateTo, setCustomDateTo] = useState(format(new Date(), 'yyyy-MM-dd'));

  // Calcular fechas según el filtro
  const dateRange = useMemo(() => {
    const today = new Date();
    switch (dateFilter) {
      case 'last_week':
        return {
          from: format(startOfWeek(subDays(today, 7), { locale: es }), 'yyyy-MM-dd'),
          to: format(endOfWeek(subDays(today, 7), { locale: es }), 'yyyy-MM-dd')
        };
      case 'last_month':
        return {
          from: format(startOfMonth(subMonths(today, 1)), 'yyyy-MM-dd'),
          to: format(endOfMonth(subMonths(today, 1)), 'yyyy-MM-dd')
        };
      case 'current_month':
        return {
          from: format(startOfMonth(today), 'yyyy-MM-dd'),
          to: format(endOfMonth(today), 'yyyy-MM-dd')
        };
      case 'all':
        // Sin filtro de fecha - mostrar todos los logs
        return {
          from: null,
          to: null
        };
      case 'custom':
        return {
          from: customDateFrom,
          to: customDateTo
        };
      default:
        // Por defecto, últimos 30 días
        return {
          from: format(subDays(today, 30), 'yyyy-MM-dd'),
          to: format(today, 'yyyy-MM-dd')
        };
    }
  }, [dateFilter, customDateFrom, customDateTo]);

  // Cargar datos de tiempo de uso
  const loadTimeUsage = async () => {
    if (!canViewTimeUsage) return;
    
    setLoading(true);
    try {
      // Si puede ver todos los usuarios, no enviar user_id para obtener datos de todos
      // Si no puede ver todos, solo enviar su propio user_id
      const params = {};
      
      // Solo agregar filtros de fecha si no es 'all'
      if (dateFilter !== 'all') {
        if (dateRange.from) {
          params.date_from = dateRange.from;
        }
        if (dateRange.to) {
          params.date_to = dateRange.to;
        }
      }
      
      // Solo agregar user_id si NO puede ver todos los usuarios
      if (!canViewAllUsersTimeUsage && user?.id) {
        params.user_id = user.id;
      }
      // Si puede ver todos, no enviar user_id para que el backend devuelva todos
      
      console.log('[AUDIT MANAGER] Cargando tiempo de uso con params:', params);
      
      const [usageData, totalData, inactiveData] = await Promise.all([
        auditService.getTimeUsageReport(params),
        auditService.getTotalUsage(params),
        auditService.getDaysWithoutActivity({ ...params, user_id: canViewAllUsersTimeUsage ? undefined : user?.id })
      ]);
      
      console.log('[AUDIT MANAGER] Tiempo de uso recibido:', usageData);
      console.log('[AUDIT MANAGER] Total uso recibido:', totalData);
      console.log('[AUDIT MANAGER] Cantidad de registros:', Array.isArray(usageData) ? usageData.length : 0);
      
      setTimeUsageData(Array.isArray(usageData) ? usageData : []);
      setTotalUsage(totalData);
      setDaysWithoutActivity(inactiveData);
    } catch (error) {
      console.error('[AUDIT MANAGER] Error cargando reporte de tiempo:', error);
      console.error('[AUDIT MANAGER] Error details:', error.response?.data || error.message);
      setTimeUsageData([]);
      setTotalUsage(null);
      setDaysWithoutActivity(null);
    } finally {
      setLoading(false);
    }
  };

  // Cargar logs de auditoría
  const loadAuditLogs = async () => {
    if (!canViewLogs) {
      console.log('[AUDIT MANAGER] No tiene permisos para ver logs');
      return;
    }
    
    setLoading(true);
    try {
      const params = {};
      
      // Solo agregar filtros de fecha si no es 'all'
      if (dateFilter !== 'all') {
        if (dateRange.from) {
          params.date_from = dateRange.from;
        }
        if (dateRange.to) {
          params.date_to = dateRange.to;
        }
      }
      
      console.log('[AUDIT MANAGER] Cargando logs con params:', params);
      console.log('[AUDIT MANAGER] Usuario actual:', user?.email);
      console.log('[AUDIT MANAGER] canViewAllUsersLogs:', canViewAllUsersLogs);
      
      // El backend ya filtra por usuario si no tiene permisos de admin
      const logs = await auditService.getAuditLogs(params);
      
      console.log('[AUDIT MANAGER] Logs recibidos:', logs);
      console.log('[AUDIT MANAGER] Tipo de logs:', typeof logs);
      console.log('[AUDIT MANAGER] Es array:', Array.isArray(logs));
      console.log('[AUDIT MANAGER] Cantidad de logs:', Array.isArray(logs) ? logs.length : (logs ? Object.keys(logs).length : 0));
      
      // Asegurar que siempre sea un array
      let logsArray = [];
      if (Array.isArray(logs)) {
        logsArray = logs;
      } else if (logs && typeof logs === 'object') {
        // Si es un objeto, intentar extraer el array
        if (Array.isArray(logs.data)) {
          logsArray = logs.data;
        } else if (Array.isArray(logs.results)) {
          logsArray = logs.results;
        } else {
          console.warn('[AUDIT MANAGER] Respuesta no es un array, convirtiendo a array:', logs);
          logsArray = [logs];
        }
      }
      
      console.log('[AUDIT MANAGER] Logs finales (array):', logsArray);
      console.log('[AUDIT MANAGER] Cantidad final:', logsArray.length);
      
      setAuditLogs(logsArray);
    } catch (error) {
      console.error('[AUDIT MANAGER] Error cargando logs de auditoría:', error);
      console.error('[AUDIT MANAGER] Error name:', error.name);
      console.error('[AUDIT MANAGER] Error message:', error.message);
      console.error('[AUDIT MANAGER] Error response:', error.response);
      console.error('[AUDIT MANAGER] Error details:', error.response?.data || error.message);
      setAuditLogs([]);
    } finally {
      setLoading(false);
    }
  };

  // Cargar usuarios online
  const loadOnlineUsers = async () => {
    if (!canViewAudit) return;
    
    setLoadingOnlineUsers(true);
    try {
      const users = await presenceService.getOnlineUsers();
      setOnlineUsers(users || []);
    } catch (error) {
      console.error('[AUDIT MANAGER] Error cargando usuarios online:', error);
      setOnlineUsers([]);
    } finally {
      setLoadingOnlineUsers(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'time-usage' && canViewTimeUsage) {
      loadTimeUsage();
    } else if (activeTab === 'audit-logs' && canViewLogs) {
      loadAuditLogs();
    } else if (activeTab === 'online-users' && canViewAudit) {
      loadOnlineUsers();
      // Actualizar cada 10 segundos cuando está en la pestaña de usuarios online
      const interval = setInterval(() => {
        loadOnlineUsers();
      }, 10000);
      return () => clearInterval(interval);
    }
  }, [activeTab, dateFilter, dateRange, canViewTimeUsage, canViewLogs, canViewAllUsersTimeUsage, canViewAllUsersLogs, canViewAudit]);

  // Formatear tiempo
  const formatTime = (seconds) => {
    if (!seconds) return '0m';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  // Formatear fecha
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd/MM/yyyy', { locale: es });
    } catch {
      return dateString;
    }
  };

  // Formatear fecha y hora
  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd/MM/yyyy HH:mm', { locale: es });
    } catch {
      return dateString;
    }
  };

  // Obtener texto del rango activo
  const getActiveRangeText = () => {
    switch (dateFilter) {
      case 'last_week':
        return 'Últimos 7 días';
      case 'last_month':
        return 'Mes pasado';
      case 'current_month':
        return 'Mes actual';
      case 'custom':
        return `${formatDate(dateRange.from)} - ${formatDate(dateRange.to)}`;
      case 'all':
        return 'Últimos 50 logs (sin filtros)';
      default:
        return 'Últimos 30 días';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex items-center gap-4">
          <div className="p-3 bg-blue-600 rounded-xl">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Auditoría y Control de Tiempo</h1>
            <p className="text-gray-500 mt-1">Control operativo y trazabilidad de acciones</p>
          </div>
        </div>
      </motion.div>

      {/* Tabs y Filtros */}
      <motion.div
        className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          {/* Tabs */}
          <div className="flex gap-2 border-b border-gray-200">
            {canViewTimeUsage && (
              <button
                onClick={() => setActiveTab('time-usage')}
                className={`px-4 py-2 font-medium transition-colors ${
                  activeTab === 'time-usage'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Clock className="w-4 h-4 inline mr-2" />
                Tiempo de Uso
              </button>
            )}
            {canViewLogs && (
              <button
                onClick={() => setActiveTab('audit-logs')}
                className={`px-4 py-2 font-medium transition-colors ${
                  activeTab === 'audit-logs'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <FileText className="w-4 h-4 inline mr-2" />
                Logs de Auditoría
              </button>
            )}
            {canViewAudit && (
              <button
                onClick={() => setActiveTab('online-users')}
                className={`px-4 py-2 font-medium transition-colors ${
                  activeTab === 'online-users'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Wifi className="w-4 h-4 inline mr-2" />
                Usuarios Online
                {onlineUsers.length > 0 && (
                  <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                    {onlineUsers.length}
                  </span>
                )}
              </button>
            )}
          </div>

          {/* Filtros de fecha */}
          <div className="flex gap-2 items-center ml-auto">
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="text-sm bg-white px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
            >
              <option value="all">Todo el Período</option>
              <option value="last_week">Última Semana</option>
              <option value="last_month">Mes Pasado</option>
              <option value="current_month">Mes Actual</option>
              <option value="custom">Rango Personalizado</option>
            </select>

            {dateFilter === 'custom' && (
              <>
                <input
                  type="date"
                  value={customDateFrom}
                  onChange={(e) => setCustomDateFrom(e.target.value)}
                  className="text-sm bg-white px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                />
                <span className="text-gray-600">a</span>
                <input
                  type="date"
                  value={customDateTo}
                  onChange={(e) => setCustomDateTo(e.target.value)}
                  className="text-sm bg-white px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                />
              </>
            )}
          </div>
        </div>

        {/* Contenido según tab activo */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600">Cargando datos...</p>
          </div>
        ) : activeTab === 'time-usage' ? (
          <TimeUsageView
            data={timeUsageData}
            totalUsage={totalUsage}
            daysWithoutActivity={daysWithoutActivity}
            formatTime={formatTime}
            formatDate={formatDate}
          />
        ) : activeTab === 'online-users' ? (
          <OnlineUsersView
            users={onlineUsers}
            loading={loadingOnlineUsers}
            onRefresh={loadOnlineUsers}
            formatDateTime={formatDateTime}
          />
        ) : (
          <AuditLogsView
            logs={auditLogs}
            formatDateTime={formatDateTime}
            activeRangeText={getActiveRangeText()}
          />
        )}
      </motion.div>
    </div>
  );
};

// Componente para vista de tiempo de uso
const TimeUsageView = ({ data, totalUsage, daysWithoutActivity, formatTime, formatDate }) => {
  return (
    <div className="space-y-6">
      {/* Estadísticas generales */}
      {totalUsage && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <motion.div
            className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm"
            whileHover={{ scale: 1.02 }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">Tiempo Total</p>
                <p className="text-2xl font-bold text-gray-800">{formatTime(totalUsage.total_seconds)}</p>
              </div>
              <Clock className="w-8 h-8 text-blue-600" />
            </div>
          </motion.div>

          <motion.div
            className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm"
            whileHover={{ scale: 1.02 }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">Slices de Actividad</p>
                <p className="text-2xl font-bold text-gray-800">{totalUsage.total_slices || 0}</p>
              </div>
              <Activity className="w-8 h-8 text-purple-600" />
            </div>
          </motion.div>

          <motion.div
            className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm"
            whileHover={{ scale: 1.02 }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">Eventos Totales</p>
                <p className="text-2xl font-bold text-gray-800">{totalUsage.total_events || 0}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-green-600" />
            </div>
          </motion.div>

          <motion.div
            className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm"
            whileHover={{ scale: 1.02 }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">Usuarios Activos</p>
                <p className="text-2xl font-bold text-gray-800">{totalUsage.unique_users || 0}</p>
              </div>
              <Users className="w-8 h-8 text-orange-600" />
            </div>
          </motion.div>
        </div>
      )}

      {/* Días sin actividad */}
      {daysWithoutActivity && daysWithoutActivity.inactive_days && daysWithoutActivity.inactive_days.length > 0 && (
        <motion.div
          className="bg-red-50 border border-red-200 rounded-xl p-4"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <h3 className="font-semibold text-red-800">Días sin Actividad</h3>
          </div>
          <p className="text-sm text-red-700 mb-2">
            {daysWithoutActivity.inactive_days.length} días sin actividad de {daysWithoutActivity.total_days} días totales
          </p>
          <div className="flex flex-wrap gap-2">
            {daysWithoutActivity.inactive_dates.slice(0, 10).map((date, idx) => (
              <span key={idx} className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs">
                {formatDate(date)}
              </span>
            ))}
            {daysWithoutActivity.inactive_dates.length > 10 && (
              <span className="px-2 py-1 text-red-600 text-xs">
                +{daysWithoutActivity.inactive_dates.length - 10} más
              </span>
            )}
          </div>
        </motion.div>
      )}

      {/* Tabla de tiempo por usuario y día */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usuario</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Tiempo Total</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Mañana</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Tarde</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Slices</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Eventos</th>
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                  No hay datos de tiempo de uso para el período seleccionado
                </td>
              </tr>
            ) : (
              data.map((item, idx) => (
                <motion.tr
                  key={idx}
                  className="border-b border-gray-100 hover:bg-gray-50"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.05 }}
                >
                  <td className="px-4 py-3 text-sm">
                    <div>
                      <div className="font-medium text-gray-900">{item.user_name}</div>
                      <div className="text-xs text-gray-500">{item.user_email}</div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{formatDate(item.date)}</td>
                  <td className="px-4 py-3 text-sm text-right font-medium">{formatTime(item.total_seconds)}</td>
                  <td className="px-4 py-3 text-sm text-right text-gray-600">{formatTime(item.morning_seconds)}</td>
                  <td className="px-4 py-3 text-sm text-right text-gray-600">{formatTime(item.afternoon_seconds)}</td>
                  <td className="px-4 py-3 text-sm text-right text-gray-600">{item.slice_count}</td>
                  <td className="px-4 py-3 text-sm text-right text-gray-600">{item.event_count}</td>
                </motion.tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Componente para vista de logs de auditoría
const OnlineUsersView = ({ users, loading, onRefresh, formatDateTime }) => {
  return (
    <div className="space-y-4">
      {/* Header con botón de refresh */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-gray-700">
              {users.length} {users.length === 1 ? 'usuario online' : 'usuarios online'}
            </span>
          </div>
        </div>
        <button
          onClick={onRefresh}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Actualizar
        </button>
      </div>

      {/* Lista de usuarios online */}
      {loading && users.length === 0 ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Cargando usuarios online...</p>
        </div>
      ) : users.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-12 text-center">
          <WifiOff className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-800 mb-2">No hay usuarios online</h3>
          <p className="text-gray-600">
            No hay usuarios conectados en este momento.
            <br />
            Los usuarios aparecerán aquí cuando inicien sesión y estén activos.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {users.map((user) => (
            <motion.div
              key={user.user_id}
              className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <h4 className="font-semibold text-gray-800">{user.user_name || 'Usuario'}</h4>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{user.user_email}</p>
                  
                  <div className="space-y-1 text-xs text-gray-500">
                    {user.last_heartbeat && (
                      <div className="flex items-center gap-1">
                        <Activity className="w-3 h-3" />
                        <span>Último heartbeat: {formatDateTime(user.last_heartbeat)}</span>
                      </div>
                    )}
                    {user.last_activity && (
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        <span>Última actividad: {formatDateTime(user.last_activity)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Información adicional */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-semibold mb-1">¿Cómo funciona?</p>
            <ul className="list-disc list-inside space-y-1 text-blue-700">
              <li>Los usuarios aparecen como "online" cuando envían un heartbeat en los últimos 60 segundos</li>
              <li>El heartbeat se envía automáticamente cada 30 segundos solo si la pestaña está visible</li>
              <li>Si un usuario cierra el navegador o no está activo, desaparecerá de esta lista automáticamente</li>
              <li>Esta lista se actualiza automáticamente cada 10 segundos</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

const AuditLogsView = ({ logs, formatDateTime, activeRangeText }) => {
  const actionColors = {
    CREATE: 'bg-green-100 text-green-700',
    UPDATE: 'bg-blue-100 text-blue-700',
    DELETE: 'bg-red-100 text-red-700',
    VIEW: 'bg-gray-100 text-gray-700',
    LOGIN: 'bg-purple-100 text-purple-700',
    LOGOUT: 'bg-orange-100 text-orange-700',
    APPROVE: 'bg-blue-100 text-blue-700',
    REJECT: 'bg-pink-100 text-pink-700',
    EXPORT: 'bg-teal-100 text-teal-700',
    IMPORT: 'bg-cyan-100 text-cyan-700',
    OTHER: 'bg-gray-100 text-gray-700',
  };

  return (
    <div className="space-y-4">
      {/* Rango activo */}
      <div className="flex items-center justify-between px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-blue-600" />
          <span className="text-sm font-medium text-blue-900">
            Rango activo: <span className="font-semibold">{activeRangeText}</span>
          </span>
        </div>
        <span className="text-xs text-blue-700">
          {logs.length} {logs.length === 1 ? 'log' : 'logs'} mostrados
        </span>
      </div>

      <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usuario</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acción</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entidad</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descripción</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha/Hora</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP</th>
          </tr>
        </thead>
        <tbody>
          {logs.length === 0 ? (
            <tr>
              <td colSpan="6" className="px-4 py-8 text-center">
                <div className="space-y-2">
                  <p className="text-gray-500">No hay logs de auditoría para el período seleccionado</p>
                  <p className="text-xs text-gray-400">
                    Nota: Solo se registran acciones importantes (crear, editar, eliminar, exportar, etc.)
                    <br />
                    Las consultas normales (GET) no se registran para evitar saturar el sistema.
                  </p>
                </div>
              </td>
            </tr>
          ) : (
            logs.map((log, idx) => (
              <motion.tr
                key={log.id || idx}
                className="border-b border-gray-100 hover:bg-gray-50"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.02 }}
              >
                <td className="px-4 py-3 text-sm">
                  <div>
                    <div className="font-medium text-gray-900">{log.user_name || 'Sistema'}</div>
                    <div className="text-xs text-gray-500">{log.user_email || ''}</div>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${actionColors[log.action] || actionColors.OTHER}`}>
                    {log.action_display || log.action}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-700">
                  {log.entity_type}
                  {log.entity_id && ` #${log.entity_id}`}
                  {log.entity_name && ` (${log.entity_name})`}
                </td>
                <td className="px-4 py-3 text-sm text-gray-700">{log.description}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{formatDateTime(log.created_at)}</td>
                <td className="px-4 py-3 text-sm text-gray-500 font-mono text-xs">{log.ip_address || '-'}</td>
              </motion.tr>
            ))
          )}
        </tbody>
      </table>
      </div>
    </div>
  );
};

export default AuditManager;

